import { Carousel, CarouselItem } from '@ixigo/iui/Carousel';
import cn from '@repo/shared/utils/cn';
import { forwardRef, useEffect, useRef } from 'react';
import { SectionHeader } from '@/components/section-header';
import type { Whybook } from '../-api/whyBook';

export interface WhyBookItem extends Whybook {
	className?: string;
}

export const FeatureCard = forwardRef<HTMLDivElement, WhyBookItem>(
	({ src, alt = '', className = '', ...rest }: WhyBookItem, ref) => {
		return (
			<div
				className={cn('h-100 w-300px rounded-20 overflow-hidden', className)}
				{...rest}
				ref={ref}
			>
				<img src={src} alt={alt} className="w-full h-full object-cover" />
			</div>
		);
	},
);

interface WhyBookSectionProps {
	itemList: Array<WhyBookItem>;
}

export function WhyBookSection({ itemList }: WhyBookSectionProps) {
	const pausedRef = useRef(false);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const activeIndexRef = useRef(0);
	const inViewRef = useRef(false);

	// Observer + autoplay combined for consistency and simpler lifecycle
	// this autoplay logic needs to be remove once this is fixed in carousel implementation in IUI
	useEffect(() => {
		if (!itemList?.length) {
			return;
		}
		const root = rootRef.current;
		const items = root
			? Array.from(root.querySelectorAll<HTMLElement>('[data-index]'))
			: [];
		if (!items.length) {
			return;
		}
		// Observe section visibility to avoid scrolling page back to this section
		const sectionObserver = new IntersectionObserver(
			([entry]) => {
				if (!entry) {
					return;
				}
				inViewRef.current = entry.isIntersecting;
			},
			{ threshold: 0.6 },
		);
		if (root) {
			sectionObserver.observe(root);
		}

		const observer = new IntersectionObserver(
			(entries) => {
				let bestIdx = -1;
				let bestRatio = 0;
				for (const entry of entries) {
					const ratio = entry.intersectionRatio;
					if (ratio > bestRatio) {
						const el = entry.target as HTMLElement;
						const idxAttr = el.getAttribute('data-index');
						const idx = idxAttr ? Number(idxAttr) : -1;
						if (idx >= 0) {
							bestIdx = idx;
							bestRatio = ratio;
						}
					}
				}
				if (bestIdx >= 0) {
					activeIndexRef.current = bestIdx;
				}
			},
			{ threshold: [0.5, 0.75, 1] },
		);
		items.forEach((el) => observer.observe(el));

		// Find the scrollable carousel container (parent of items with horizontal scroll)
		const getScrollableParent = (element: HTMLElement): HTMLElement | null => {
			let parent = element.parentElement;
			while (parent) {
				const style = getComputedStyle(parent);
				const overflowX = style.overflowX;
				if (overflowX === 'auto' || overflowX === 'scroll') {
					return parent;
				}
				parent = parent.parentElement;
			}
			return null;
		};

		const carouselContainer = items[0] ? getScrollableParent(items[0]) : null;

		const autoplayId = setInterval(() => {
			// pause when user interacting, section offscreen, or tab hidden
			if (pausedRef.current || !inViewRef.current || document.hidden) {
				return;
			}
			if (!items.length || !carouselContainer) {
				return;
			}
			const nextIndex = (activeIndexRef.current + 1) % items.length;
			const nextItem = items[nextIndex];

			if (nextItem) {
				// Use getBoundingClientRect for accurate position calculation
				const containerRect = carouselContainer.getBoundingClientRect();
				const itemRect = nextItem.getBoundingClientRect();

				// Calculate scroll delta: how far the item is from container's left edge
				const scrollDelta = itemRect.left - containerRect.left;
				const targetScrollLeft = carouselContainer.scrollLeft + scrollDelta;

				// Scroll only the carousel container (no vertical page scroll)
				carouselContainer.scrollTo({
					left: targetScrollLeft,
					behavior: 'smooth',
				});
			}

			activeIndexRef.current = nextIndex;
		}, 3000);

		return () => {
			clearInterval(autoplayId);
			observer.disconnect();
			sectionObserver.disconnect();
		};
	}, [itemList?.length]);

	// Pause on user interaction (touch/mouse), resume on end
	useEffect(() => {
		const root = rootRef.current;
		if (!root) {
			return;
		}
		const pause = () => {
			pausedRef.current = true;
		};
		const resume = () => {
			pausedRef.current = false;
		};
		root.addEventListener('touchstart', pause, { passive: true });
		root.addEventListener('touchend', resume);
		root.addEventListener('mouseenter', pause);
		root.addEventListener('mouseleave', resume);
		return () => {
			root.removeEventListener('touchstart', pause);
			root.removeEventListener('touchend', resume);
			root.removeEventListener('mouseenter', pause);
			root.removeEventListener('mouseleave', resume);
		};
	}, []);

	return (
		<div className="bg-primary py-20 pr-0 flex flex-col gap-y-10" ref={rootRef}>
			<SectionHeader title="Why Book Cabs With ixigo?" className="pl-20" />
			<Carousel scrollSnapAlign="start" gap="0">
				{itemList.filter(Boolean).map((item, index) => (
					<CarouselItem key={item.src} className="pl-20 last:mr-20">
						<FeatureCard {...item} data-index={index} />
					</CarouselItem>
				))}
			</Carousel>
		</div>
	);
}
